import moment from 'moment';
import Swal from 'sweetalert2';
import { displayError } from '../helpers';
import { getCustomerDataTransactionDetails } from '../services/ReportServices';
import { getHTMLForTablePopup } from './PopupUtils';
import { openPdfInApp } from './openPdfInApp';
import { buildCustomerLedgerPdfHeader } from './customerLedgerPdf';
import {
    createTransactionDetailPdfBlob,
    exportTransactionDetailCsv,
    exportTransactionDetailExcel,
} from './customerDataReportExport';

export function formatAmount(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) {
        return '0.00';
    }
    return number.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatQty(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) {
        return '0';
    }
    return Number.isInteger(number) ? String(number) : number.toFixed(3).replace(/\.?0+$/, '');
}

export function sanitizeFilenamePart(value) {
    return String(value || '')
        .replace(/[^\w\s-]/g, '')
        .trim()
        .replace(/\s+/g, '_')
        .slice(0, 40) || 'details';
}

function sumQty(details, field) {
    return formatQty(details.reduce((sum, item) => sum + (Number(item[field]) || 0), 0));
}

function sumAmount(details, field) {
    return formatAmount(details.reduce((sum, item) => sum + (Number(item[field]) || 0), 0));
}

export function showTransactionPopup({
    title,
    subtitle,
    columns,
    details,
    footerRow,
    emptyMessage,
    width = '960px',
    exportContext = null,
}) {
    const downloadBar = exportContext ? `
        <div style="display:flex;justify-content:flex-end;gap:8px;margin-bottom:12px;flex-wrap:wrap;">
            <button type="button" id="tx-popup-export-excel" style="padding:6px 14px;border:1px solid #198754;background:#198754;color:#fff;border-radius:6px;font-size:13px;cursor:pointer;">
                Excel
            </button>
            <button type="button" id="tx-popup-export-pdf" style="padding:6px 14px;border:1px solid #dc3545;background:#dc3545;color:#fff;border-radius:6px;font-size:13px;cursor:pointer;">
                PDF
            </button>
            <button type="button" id="tx-popup-export-csv" style="padding:6px 14px;border:1px solid #6c757d;background:#6c757d;color:#fff;border-radius:6px;font-size:13px;cursor:pointer;">
                CSV
            </button>
        </div>
    ` : '';

    Swal.fire({
        title,
        html: `${downloadBar}${getHTMLForTablePopup(subtitle, columns, details, footerRow, emptyMessage)}`,
        width,
        confirmButtonText: 'Close',
        didOpen: () => {
            if (!exportContext) {
                return;
            }
            const payload = {
                title,
                subtitle,
                columns,
                records: details,
                footerRow,
                periodFrom: exportContext.periodFrom,
                periodTo: exportContext.periodTo,
                generatedAt: exportContext.generatedAt,
                filename: exportContext.filenameBase,
            };

            document.getElementById('tx-popup-export-excel')?.addEventListener('click', () => {
                exportTransactionDetailExcel({
                    ...payload,
                    filename: `${exportContext.filenameBase}.xlsx`,
                });
            });

            document.getElementById('tx-popup-export-csv')?.addEventListener('click', () => {
                exportTransactionDetailCsv({
                    columns,
                    records: details,
                    footerRow,
                    filename: `${exportContext.filenameBase}.csv`,
                });
            });

            document.getElementById('tx-popup-export-pdf')?.addEventListener('click', async () => {
                try {
                    Swal.fire({
                        title: 'Preparing PDF...',
                        allowOutsideClick: false,
                        didOpen: () => Swal.showLoading(),
                    });
                    const header = exportContext.cookies
                        ? await buildCustomerLedgerPdfHeader(exportContext.cookies, [], [], {
                            documentTitle: title,
                            subtitle,
                        })
                        : null;
                    const blob = createTransactionDetailPdfBlob({ ...payload, header });
                    Swal.close();
                    openPdfInApp(blob, {
                        title,
                        downloadFileName: `${exportContext.filenameBase}.pdf`,
                    });
                } catch (error) {
                    Swal.close();
                    displayError({ message: error?.message || 'PDF export failed.' }, 'Export Failed');
                }
            });
        },
    });
}

export const TRANSACTION_POPUP_CONFIG = {
    quotation: {
        title: 'Quotation Details',
        popupWidth: '1280px',
        emptyMessage: 'No quotations found for this customer/site.',
        columns: [
            { key: 'quotation_no', label: 'Quotation No' },
            { key: 'quotation_date', label: 'Date' },
            { key: 'customer_name', label: 'Customer' },
            { key: 'site_name', label: 'Site' },
            { key: 'grade', label: 'Grade' },
            { key: 'qty', label: 'Qty', align: 'right' },
            { key: 'rate', label: 'Rate', align: 'right' },
            { key: 'amount', label: 'Amount', align: 'right' },
            { key: 'delivery_mode', label: 'Mode of Delivery' },
            { key: 'distance', label: 'Distance', align: 'right' },
            { key: 'marketing_name', label: 'Marketing' },
        ],
        mapDetails: (rawDetails) => rawDetails.map((item) => ({
            ...item,
            qty: formatQty(item.qty),
            rate: formatAmount(item.rate),
            amount: formatAmount(item.amount),
            distance: item.distance === '' || item.distance === null || item.distance === undefined
                ? ''
                : formatQty(item.distance),
        })),
        footerRow: (rawDetails) => ({
            quotation_no: 'TOTAL',
            qty: sumQty(rawDetails, 'qty'),
            amount: sumAmount(rawDetails, 'amount'),
        }),
    },
    sales_order: {
        title: 'Sales Order Details',
        popupWidth: '1400px',
        emptyMessage: 'No sales orders found for this customer/site.',
        columns: [
            { key: 'sales_order_no', label: 'Sales Order No' },
            { key: 'sales_order_date', label: 'Order Date' },
            { key: 'customer_name', label: 'Customer' },
            { key: 'site_name', label: 'Site' },
            { key: 'grade', label: 'Grade' },
            { key: 'rate', label: 'Rate', align: 'right' },
            { key: 'qty', label: 'Qty', align: 'right' },
            { key: 'amount', label: 'Amount', align: 'right' },
            { key: 'transport_mode', label: 'Mode of Delivery' },
            { key: 'marketing_name', label: 'Marketing' },
        ],
        mapDetails: (rawDetails) => rawDetails.map((item) => ({
            ...item,
            qty: formatQty(item.qty),
            rate: formatAmount(item.rate),
            amount: formatAmount(item.amount),
        })),
        footerRow: (rawDetails) => ({
            sales_order_no: 'TOTAL',
            qty: sumQty(rawDetails, 'qty'),
            amount: sumAmount(rawDetails, 'amount'),
        }),
    },
    schedule: {
        title: 'Schedule Details',
        popupWidth: '1280px',
        emptyMessage: 'No schedules found for this customer/site.',
        columns: [
            { key: 'schedule_no', label: 'Schedule No' },
            { key: 'schedule_date', label: 'Schedule Date' },
            { key: 'delivery_date', label: 'Delivery Date' },
            { key: 'delivery_time', label: 'Time' },
            { key: 'customer_name', label: 'Customer' },
            { key: 'site_name', label: 'Site' },
            { key: 'grade', label: 'Grade' },
            { key: 'qty', label: 'Qty', align: 'right' },
            { key: 'delivery_mode', label: 'Mode' },
            { key: 'plant_name', label: 'Plant' },
            { key: 'ws_status', label: 'Status' },
        ],
        mapDetails: (rawDetails) => rawDetails.map((item) => ({
            ...item,
            qty: formatQty(item.qty),
        })),
        footerRow: (rawDetails) => ({
            schedule_no: 'TOTAL',
            qty: sumQty(rawDetails, 'qty'),
        }),
    },
    design_mix: {
        title: 'Design Mix Details',
        popupWidth: '1200px',
        emptyMessage: 'No design mix found for this customer/site.',
        columns: [
            { key: 'recipe_code', label: 'Recipe' },
            { key: 'schedule_no', label: 'Schedule No' },
            { key: 'delivery_date', label: 'Delivery Date' },
            { key: 'customer_name', label: 'Customer' },
            { key: 'site_name', label: 'Site' },
            { key: 'grade', label: 'Grade' },
            { key: 'density', label: 'Density', align: 'right' },
            { key: 'water_cem', label: 'W/C', align: 'right' },
            { key: 'total_cem', label: 'Total Cement', align: 'right' },
        ],
        mapDetails: (rawDetails) => rawDetails.map((item) => ({
            ...item,
            density: formatQty(item.density),
            water_cem: formatQty(item.water_cem),
        })),
        footerRow: () => null,
    },
    delivery_challan: {
        title: 'Delivery Challan Details',
        popupWidth: '1400px',
        emptyMessage: 'No delivery challans found for this customer/site.',
        columns: [
            { key: 'delivery_challan_no', label: 'DC No' },
            { key: 'delivery_challan_date', label: 'Date' },
            { key: 'customer_name', label: 'Customer' },
            { key: 'site_name', label: 'Site' },
            { key: 'grade', label: 'Grade' },
            { key: 'qty', label: 'Qty', align: 'right' },
            { key: 'rate', label: 'Rate', align: 'right' },
            { key: 'amount', label: 'Amount', align: 'right' },
            { key: 'delivery_mode', label: 'Mode of Delivery' },
            { key: 'distance', label: 'Distance', align: 'right' },
            { key: 'vehicle_name', label: 'Vehicle' },
            { key: 'driver_name', label: 'Driver' },
            { key: 'total_km', label: 'KM', align: 'right' },
            { key: 'marketing_name', label: 'Marketing' },
        ],
        mapDetails: (rawDetails) => rawDetails.map((item) => ({
            ...item,
            qty: formatQty(item.qty),
            rate: formatAmount(item.rate),
            amount: formatAmount(item.amount),
            distance: item.distance === '' || item.distance === null || item.distance === undefined
                ? ''
                : formatQty(item.distance),
            total_km: item.total_km === '' || item.total_km === null || item.total_km === undefined
                ? ''
                : formatQty(item.total_km),
        })),
        footerRow: (rawDetails) => ({
            delivery_challan_no: 'TOTAL',
            qty: sumQty(rawDetails, 'qty'),
            amount: sumAmount(rawDetails, 'amount'),
            total_km: sumQty(rawDetails, 'total_km'),
        }),
    },
    invoice: {
        title: 'Invoice Details',
        popupWidth: '1200px',
        emptyMessage: 'No invoices found for this customer/site.',
        columns: [
            { key: 'invoice_no', label: 'Invoice No' },
            { key: 'invoice_date', label: 'Date' },
            { key: 'customer_name', label: 'Customer' },
            { key: 'site_name', label: 'Site' },
            { key: 'qty', label: 'Qty', align: 'right' },
            { key: 'taxable_amt', label: 'Taxable', align: 'right' },
            { key: 'gst_amt', label: 'GST', align: 'right' },
            { key: 'invoice_amt', label: 'Invoice Amt', align: 'right' },
            { key: 'period_from', label: 'Period From' },
            { key: 'period_to', label: 'Period To' },
        ],
        mapDetails: (rawDetails) => rawDetails.map((item) => ({
            ...item,
            qty: formatQty(item.qty),
            taxable_amt: formatAmount(item.taxable_amt),
            gst_amt: formatAmount(item.gst_amt),
            invoice_amt: formatAmount(item.invoice_amt),
        })),
        footerRow: (rawDetails) => ({
            invoice_no: 'TOTAL',
            qty: sumQty(rawDetails, 'qty'),
            taxable_amt: sumAmount(rawDetails, 'taxable_amt'),
            gst_amt: sumAmount(rawDetails, 'gst_amt'),
            invoice_amt: sumAmount(rawDetails, 'invoice_amt'),
        }),
    },
};

export function openCustomerSiteDocuments({
    cookies,
    transactionType,
    customerId,
    consigneeId,
    customerName,
    projectName,
    fromDate,
    toDate,
}) {
    const config = TRANSACTION_POPUP_CONFIG[transactionType];
    if (!config) {
        return;
    }
    if (!customerId) {
        Swal.fire('Save the customer first to view documents.', '', 'info');
        return;
    }
    if (!consigneeId) {
        Swal.fire('Save this site first to view documents.', '', 'info');
        return;
    }

    Swal.fire({
        title: 'Loading...',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
    });

    const params = {
        type: transactionType,
        customer_id: customerId,
        consignee_id: consigneeId,
    };
    if (fromDate && toDate) {
        params.from_date = fromDate;
        params.upto_date = toDate;
    }

    getCustomerDataTransactionDetails(cookies, params)
        .then((response) => {
            Swal.close();
            const rawDetails = response.details || [];
            if (!rawDetails.length) {
                Swal.fire(config.emptyMessage, '', 'info');
                return;
            }
            const subtitle = `${customerName || ''}${projectName ? ` — ${projectName}` : ''}`;
            const periodFrom = fromDate || 'All';
            const periodTo = toDate || 'All';
            const filenameBase = `${transactionType}_${sanitizeFilenamePart(customerName)}_${sanitizeFilenamePart(projectName || 'site')}`;
            showTransactionPopup({
                title: config.title,
                subtitle,
                columns: config.columns,
                details: config.mapDetails(rawDetails),
                footerRow: config.footerRow(rawDetails),
                emptyMessage: config.emptyMessage,
                width: config.popupWidth || '960px',
                exportContext: {
                    cookies,
                    filenameBase,
                    periodFrom,
                    periodTo,
                    generatedAt: moment().format('DD-MM-YYYY HH:mm'),
                },
            });
        })
        .catch((error) => {
            Swal.close();
            displayError(error?.response?.data || error, 'Failed to load details');
        });
}
